import { useCallback, useEffect, useRef } from 'react';
import { Column, FilterProps, Table } from '@tanstack/react-table';
import { EMPTY_FILTER_VALUE } from '@sib/api/constants';
import { useTranslation } from '@sib/i18n';
import { TableFilterMetaEntry } from '../../context';
import { getTableColumnPinningProperties } from '../../utils';

export const useGetTableColumnOptions = () => {
  const { t } = useTranslation();

  const getTableColumnOptions = useCallback(
    <T>(column: Column<T>) => {
      const pinnable = column.columnDef.pinnable ?? true;
      const hasFilter = Boolean(column.columnDef.filter);
      const enableShowEmpty =
        column.columnDef.filter?.enableShowEmpty !== false;

      const pinningOptions: { label: string; onClick: () => void }[] = pinnable
        ? (() => {
            const { isLeftPinned, isRightPinned } =
              getTableColumnPinningProperties(column);

            return [
              isLeftPinned
                ? {
                    label: t('pinning.unpin'),
                    onClick: () => column.pin(false),
                  }
                : {
                    label: t('pinning.toLeft'),
                    onClick: () => column.pin('left'),
                  },
              isRightPinned
                ? {
                    label: t('pinning.unpin'),
                    onClick: () => column.pin(false),
                  }
                : {
                    label: t('pinning.toRight'),
                    onClick: () => column.pin('right'),
                  },
            ];
          })()
        : [];

      const filterOptions =
        hasFilter && enableShowEmpty
          ? [
              column.getFilterValue() === EMPTY_FILTER_VALUE
                ? {
                    label: t('components.dataTable.emptyFilter.clear'),
                    onClick: () => column.setFilterValue(undefined),
                  }
                : {
                    label: t('components.dataTable.emptyFilter.apply'),
                    onClick: () => column.setFilterValue(EMPTY_FILTER_VALUE),
                  },
            ]
          : [];

      return [...pinningOptions, ...filterOptions];
    },
    [t]
  );

  return getTableColumnOptions;
};

interface UseResizeConstraintsProps<T> {
  table: Table<T>;
}

export const useResizeConstraints = <T>({
  table,
}: UseResizeConstraintsProps<T>) => {
  const resizeJustEndedRef = useRef(false);
  const prevResizingColumnRef = useRef(
    table.getState().columnSizingInfo.isResizingColumn
  );

  const { columnSizingInfo } = table.getState();
  const isResizing = columnSizingInfo.isResizingColumn;

  useEffect(() => {
    if (prevResizingColumnRef.current && !isResizing)
      resizeJustEndedRef.current = true;

    prevResizingColumnRef.current = isResizing ?? null;
  }, [isResizing]);

  return {
    resizeJustEndedRef,
    prevResizingColumnRef,
    columnSizingInfo,
    isResizing,
  };
};

export const resolveFilterProps = (
  filterProps: FilterProps | undefined,
  meta: TableFilterMetaEntry | undefined
): FilterProps | undefined => {
  if (!filterProps || !meta) return filterProps;

  if (
    (filterProps.type === 'select' ||
      filterProps.type === 'single_select' ||
      filterProps.type === 'ci_select' ||
      filterProps.type === 'ci_single_select') &&
    !filterProps.options &&
    meta.options
  )
    return { ...filterProps, options: meta.options };

  if (filterProps.type === 'number_range' && !filterProps.ranges && meta.ranges)
    return { ...filterProps, ranges: meta.ranges };

  return filterProps;
};
